import { Controller } from '@hotwired/stimulus'

// Speed up large banner uploads without touching images that are already web-ready.
export default class extends Controller {
  static targets = ['form', 'slidesInput', 'imageInput', 'status', 'submit']

  static values = {
    maxWidth: { type: Number, default: 2100 },
    maxHeight: { type: Number, default: 700 },
    quality: { type: Number, default: 0.92 },
    skipBelowBytes: { type: Number, default: 1_500_000 }
  }

  connect () {
    this.busy = false
    this.allowSubmit = false
  }

  async slidesSelected () {
    if (!this.hasSlidesInputTarget) return
    await this.optimizeInput(this.slidesInputTarget)
  }

  async imageSelected () {
    if (!this.hasImageInputTarget) return
    await this.optimizeInput(this.imageInputTarget)
  }

  async submit (event) {
    // The programmatic requestSubmit() below re-fires this event; let it pass through.
    if (this.allowSubmit) return
    if (this.busy) {
      event.preventDefault()
      return
    }

    this.busy = true
    event.preventDefault()
    this.setStatus('Checking images…')
    this.toggleSubmit(true)

    try {
      if (this.hasSlidesInputTarget) await this.optimizeInput(this.slidesInputTarget)
      if (this.hasImageInputTarget && this.imageInputTarget.files?.length) {
        await this.optimizeInput(this.imageInputTarget)
      }

      this.setStatus('Uploading…')
      this.allowSubmit = true
      this.formTarget.requestSubmit()
    } catch (_error) {
      this.setStatus('Could not prepare images. Try smaller JPG/PNG files.')
      this.toggleSubmit(false)
      this.busy = false
      this.allowSubmit = false
    }
  }

  async optimizeInput (input) {
    const files = Array.from(input.files || [])
    if (files.length === 0) return

    const optimized = []
    let changed = 0

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i]
      const next = await this.prepareFile(file)
      optimized.push(next)
      if (next !== file) changed += 1
    }

    const transfer = new DataTransfer()
    optimized.forEach((file) => transfer.items.add(file))
    input.files = transfer.files

    if (changed > 0) {
      const saved = files.reduce((sum, file, index) => sum + Math.max(0, file.size - optimized[index].size), 0)
      this.setStatus(saved > 50_000
        ? `Optimized ${changed} large image${changed > 1 ? 's' : ''} for faster upload (saved ${this.formatBytes(saved)})`
        : `Optimized ${changed} oversized image${changed > 1 ? 's' : ''} to fit the banner`)
    } else if (!this.busy) {
      this.setStatus('')
    }
  }

  async prepareFile (file) {
    if (!this.looksLikeImage(file) || file.type === 'image/gif') return file

    const dimensions = await this.readDimensions(file)
    if (!dimensions) return file

    const needsResize = dimensions.width > this.maxWidthValue || dimensions.height > this.maxHeightValue
    const tooLarge = file.size > this.skipBelowBytesValue

    // Already good for the homepage banner, so keep original quality.
    if (!needsResize && !tooLarge) return file

    try {
      return await this.compress(file, dimensions, { needsResize, tooLarge })
    } catch (_error) {
      return file
    }
  }

  async compress (file, dimensions, { needsResize, tooLarge }) {
    const source = await this.loadImage(file)
    const targetWidth = needsResize ? this.fitWithin(dimensions.width, dimensions.height, this.maxWidthValue, this.maxHeightValue).width : dimensions.width
    const targetHeight = needsResize ? this.fitWithin(dimensions.width, dimensions.height, this.maxWidthValue, this.maxHeightValue).height : dimensions.height

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    canvas.getContext('2d').drawImage(source, 0, 0, targetWidth, targetHeight)

    if (source.close) source.close()

    // Huge PNG/WebP → high-quality JPG. Resize-only → keep PNG when possible.
    const useJpeg = tooLarge || file.type === 'image/jpeg' || file.type === 'image/jpg'
    const mime = useJpeg ? 'image/jpeg' : 'image/png'
    const ext = useJpeg ? 'jpg' : 'png'

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result)
        else reject(new Error('compress failed'))
      }, mime, useJpeg ? this.qualityValue : undefined)
    })

    const base = (file.name || 'banner').replace(/\.[^.]+$/, '')
    return new File([blob], `${base}.${ext}`, { type: mime, lastModified: Date.now() })
  }

  fitWithin (width, height, maxWidth, maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1)
    return {
      width: Math.max(1, Math.round(width * ratio)),
      height: Math.max(1, Math.round(height * ratio))
    }
  }

  loadImage (file) {
    if (window.createImageBitmap) {
      return createImageBitmap(file)
    }

    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve(img)
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('unreadable'))
      }
      img.src = url
    })
  }

  readDimensions (file) {
    return new Promise((resolve) => {
      if (!window.createImageBitmap) {
        this.loadImage(file).then((img) => {
          resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height })
        }).catch(() => resolve(null))
        return
      }

      createImageBitmap(file).then((bitmap) => {
        const dimensions = { width: bitmap.width, height: bitmap.height }
        bitmap.close()
        resolve(dimensions)
      }).catch(() => resolve(null))
    })
  }

  looksLikeImage (file) {
    if (file.type?.startsWith('image/')) return true
    const ext = (file.name || '').split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'heic', 'heif', 'avif'].includes(ext)
  }

  setStatus (message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle('hidden', !message)
  }

  toggleSubmit (disabled) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = disabled
    this.submitTarget.classList.toggle('opacity-60', disabled)
    this.submitTarget.classList.toggle('cursor-wait', disabled)
  }

  formatBytes (bytes) {
    if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }
}
